import React from "react";
import { BottomNavigation, BottomNavigationAction, Paper, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useSimpleDeviceDetection } from '../hooks/useDeviceDetection';
import Image from 'next/image';

interface ButtomMenuBarProps {
    onHomeRequest?: () => void;
    onMeRequest?: () => void;
    onRequirementClick?: () => void;
    onChatClick?: () => void;
}

// Custom icon components using SVG from Figma design
const HomeIcon = () => (
    <Image 
        src="/assets/icons/bottom-menu/home-icon.svg" 
        alt="Home" 
        width={26} 
        height={25} 
    />
);

const RequirementIcon = () => (
    <div style={{ position: 'relative', width: 25, height: 25 }}>
        <Image 
            src="/assets/icons/bottom-menu/requirement-icon-1.svg" 
            alt="Requirement" 
            width={25} 
            height={25} 
            style={{ position: 'absolute', top: 0, left: 0 }}
        />
        <Image 
            src="/assets/icons/bottom-menu/requirement-icon-2.svg" 
            alt="" 
            width={12.2} 
            height={1.7} 
            style={{ position: 'absolute', top: 12.7, left: 5.8 }}
        />
        <Image 
            src="/assets/icons/bottom-menu/requirement-icon-3.svg" 
            alt="" 
            width={12.2} 
            height={1.7} 
            style={{ position: 'absolute', top: 8.1, left: 5.8 }}
        />
        <Image 
            src="/assets/icons/bottom-menu/requirement-icon-4.svg" 
            alt="" 
            width={7.6} 
            height={1.8} 
            style={{ position: 'absolute', top: 17.4, left: 5.8 }}
        />
    </div>
);

const SellIcon = () => (
    <div style={{ position: 'relative', width: 19, height: 25 }}>
        <Image 
            src="/assets/icons/bottom-menu/sell-icon-1.svg" 
            alt="Sell" 
            width={16.6} 
            height={7.9} 
            style={{ position: 'absolute', top: 13.5, left: 1.1 }}
        />
        <Image 
            src="/assets/icons/bottom-menu/sell-icon-2.svg" 
            alt="" 
            width={16.6} 
            height={7.9} 
            style={{ position: 'absolute', top: 1.1, left: 1.1 }}
        />
    </div>
);

const RentIcon = () => (
    <Image 
        src="/assets/icons/bottom-menu/rent-icon.svg" 
        alt="Rent" 
        width={21} 
        height={9} 
        style={{ marginTop: 8 }}
    />
);

const ProfileIcon = () => (
    <div style={{ position: 'relative', width: 22, height: 25 }}>
        <Image 
            src="/assets/icons/bottom-menu/profile-icon-1.svg" 
            alt="Profile" 
            width={13.5} 
            height={13.2} 
            style={{ position: 'absolute', top: 0, left: 4.5 }}
        />
        <Image 
            src="/assets/icons/bottom-menu/profile-icon-2.svg" 
            alt="" 
            width={22} 
            height={10.4} 
            style={{ position: 'absolute', top: 14.6, left: 0 }}
        />
    </div>
);

export const ButtomMenuBar = ({ onMeRequest, onRequirementClick, onChatClick }: ButtomMenuBarProps) => {
    const { isMobile } = useSimpleDeviceDetection();
    const { t, i18n } = useTranslation('common');
    const router = useRouter();

    const onLinkMenuClicked = (type: string) => {
        if (type === 'default') {
            router.push('/');
            return;
        }

        const url = i18n.language === 'th' ? `/${t(type)}/${t('estate')}` : `/${i18n.language}/${t(type)}/${t('estate')}`;
        router.push(url);
        // window.location.href = url;
    }
    
    return isMobile ? (<>
    <Paper sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1001,
        boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)'
    }} elevation={6}>
        <BottomNavigation 
            showLabels 
            sx={{
                padding: '8px 10px',
                gap: '32px',
                justifyContent: 'space-between'
            }}
        >
            <BottomNavigationAction 
                label={t('bottomMenu.home')} 
                icon={<HomeIcon />}  
                sx={{ 
                    pl: 1.5, 
                    pr: 0.5,
                    '& .MuiBottomNavigationAction-label': {
                        fontFamily: 'Prompt',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '1.25em',
                        textAlign: 'center',
                        marginTop: '5px'
                    }
                }} 
                onClick={() => { onLinkMenuClicked('default'); }} 
            />
            <BottomNavigationAction 
                label={t('bottomMenu.require')} 
                icon={<RequirementIcon />} 
                sx={{ 
                    pl: 0.5, 
                    pr: 0.5,
                    '& .MuiBottomNavigationAction-label': {
                        fontFamily: 'Prompt',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '1.25em',
                        textAlign: 'center',
                        marginTop: '5px'
                    }
                }} 
                onClick={() => { onRequirementClick?.() }} 
            />
            <BottomNavigationAction 
                label={t('bottomMenu.sell')} 
                icon={<SellIcon />} 
                sx={{ 
                    pl: 0.5, 
                    pr: 0.5,
                    '& .MuiBottomNavigationAction-label': {
                        fontFamily: 'Prompt',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '1.25em',
                        textAlign: 'center',
                        marginTop: '5px'
                    }
                }} 
                onClick={() => { onLinkMenuClicked('link.sell'); }} 
            />
            <BottomNavigationAction 
                label={t('bottomMenu.rent')} 
                icon={<RentIcon />}  
                sx={{ 
                    pl: 0.5, 
                    pr: 0.5,
                    '& .MuiBottomNavigationAction-label': {
                        fontFamily: 'Prompt',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '1.25em',
                        textAlign: 'center',
                        marginTop: '5px'
                    }
                }} 
                onClick={() => { onLinkMenuClicked('link.rent'); }}
            />
            {/* <BottomNavigationAction label="Chat" icon={<FontAwesomeIcon icon={faComment} size="lg" />} onClick={() => { onChatClick?.(); }} /> */}
            <BottomNavigationAction 
                label={t('bottomMenu.me')} 
                icon={<ProfileIcon />} 
                sx={{ 
                    pl: 0.5, 
                    pr: 1.5,
                    '& .MuiBottomNavigationAction-label': {
                        fontFamily: 'Prompt',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '1.25em',
                        textAlign: 'center',
                        marginTop: '5px'
                    }
                }} 
                onClick={() => { onMeRequest?.(); }} 
            />
        </BottomNavigation>
    </Paper>
    </>) : <></>;
}